import React from "react";
import { useFormContext, Controller } from "react-hook-form";

import {
  CircleRadio,
  CustomSelectDropdown,
  DarkFormField,
  DarkTextArea,
  CustomCheckbox,
  FileInput,
} from "../../atoms/Inputs";
import { selectStyle } from "../../../shared/fixtures/select_style";
import { P } from "../../atoms/Texts";

import { ValidationEnum } from "../../organisms/DnDFormBuilder/validationEnum";

const ErrorWrapper = ({ name, children }) => {
  const {
    formState: { errors },
  } = useFormContext();

  return (
    <div>
      {children}
      {errors[name]?.message && (
        <P fontSize="12px" className="-mt-4 mb-4 text-red-400 text-sm">
          {errors[name]?.message}
        </P>
      )}
    </div>
  );
};

const FormField = ({ field, control }) => {
  if (["text"].includes(field.type)) {
    return (
      <>
        {field.label && (
          <label>
            <P fontSize="14px" fontFamily="semi" color="#47495C" className="mb-2">
              {field.label}
              {field.required && (
                <span className="" style={{ color: "#E62250" }}>
                  *
                </span>
              )}
            </P>
          </label>
        )}
        <Controller
          name={field.name}
          control={control}
          rules={ValidationEnum(field)}
          render={({ field: formField }) => {
            return (
              <DarkFormField
                {...formField}
                type={field?.subtype || field.type}
                placeholder={field?.placeholder || ""}
                className="w-full"
              />
            );
          }}
        />
      </>
    );
  }

  if (["number", "date"].includes(field.type)) {
    return (
      <>
        {field.label && (
          <label>
            <P fontSize="14px" fontFamily="semi" color="#47495C" className="mb-2">
              {field.label}
              {field.required && (
                <span className="" style={{ color: "#E62250" }}>
                  *
                </span>
              )}
            </P>
          </label>
        )}

        <Controller
          name={field.name}
          control={control}
          rules={ValidationEnum(field)}
          render={({ field: formField }) => (
            <DarkFormField
              type={field.type}
              placeholder={field?.placeholder || ""}
              {...formField}
              className="w-full"
            />
          )}
        />
      </>
    );
  }

  if (field.type === "textarea") {
    return (
      <>
        {field.label && (
          <label>
            <P fontSize="14px" fontFamily="semi" color="#47495C" className="mb-2">
              {field.label}
              {field.required && (
                <span className="" style={{ color: "#E62250" }}>
                  *
                </span>
              )}
            </P>
          </label>
        )}
        <Controller
          name={field.name}
          control={control}
          rules={ValidationEnum(field)}
          render={({ field: formField }) => (
            <DarkTextArea
              rows={3}
              type={field.type}
              {...formField}
              placeholder={field?.placeholder || ""}
              className="w-full"
            />
          )}
        />
      </>
    );
  }

  if (field.type === "file") {
    return (
      <>
        {field.label && (
          <label>
            <P fontSize="14px" fontFamily="semi" color="#47495C" className="mb-2">
              {field.label}
              {field.required && (
                <span className="" style={{ color: "#E62250" }}>
                  *
                </span>
              )}
            </P>
          </label>
        )}

        <Controller
          name={field.name}
          control={control}
          rules={{
            required: field.required && "This field is required",
            validate: (value) => {
              if (!value) return true;

              // Add file type validation
              if (field.accept) {
                const allowedTypes = field.accept
                  .split(",")
                  .map((type) => type.trim());
                const fileType = value.type;
                if (
                  !allowedTypes.some((type) =>
                    fileType.match(new RegExp(type.replace("*", ".*"))),
                  )
                ) {
                  return `Please upload a valid file type (${field.accept})`;
                }
              }

              // Add file size validation (e.g., max 5MB)
              if (field.maxSize) {
                const maxSize = field.maxSize * 1024 * 1024; // Convert MB to bytes
                if (value.size > maxSize) {
                  return `File size should be less than ${field.maxSize}MB`;
                }
              }

              return true;
            },
          }}
          render={({ field: { onChange } }) => (
            <FileInput
              id={field.name}
              onChange={onChange}
              required={field.required}
              accept={field.accept}
              className="w-full"
            />
          )}
        />
      </>
    );
  }

  if (field.type === "select") {
    return (
      <>
        {field.label && (
          <label>
            <P fontSize="14px" fontFamily="semi" color="#47495C" className="mb-2">
              {field.label}
              {field.required && (
                <span className="" style={{ color: "#E62250" }}>
                  *
                </span>
              )}
            </P>
          </label>
        )}
        <Controller
          name={field.name}
          control={control}
          rules={{
            required: field.required ? "Please select an option" : false,
          }}
          render={({ field: formField }) => (
            <CustomSelectDropdown
              {...formField}
              control={control}
              styles={selectStyle}
              options={field.values}
            />
          )}
        />
      </>
    );
  }

  if (field.type === "radio-group") {
    return (
      <>
        {field.label && (
          <label>
            <P fontSize="14px" fontFamily="semi" color="#47495C" className="mb-2">
              {field.label}
              {field.required && (
                <span className="" style={{ color: "#E62250" }}>
                  *
                </span>
              )}
            </P>
          </label>
        )}

        {field?.values?.map((option, index) => (
          <Controller
            key={index}
            id={`${field.name}-${option.value}`}
            name={field.name}
            control={control}
            rules={{
              required: field.required ? "Please select an option" : false,
            }}
            render={({ field: formField }) => (
              <CircleRadio
                name={field.name}
                label={option.label}
                value={option.value}
                checked={formField.value === option.value}
                onChange={() => formField.onChange(option.value)}
              />
            )}
          />
        ))}
      </>
    );
  }

  if (field.type === "checkbox-group") {
    return (
      <>
        {field.label && (
          <label>
            <P fontSize="14px" fontFamily="semi" color="#47495C" className="mb-2">
              {field.label}
              {field.required && (
                <span className="" style={{ color: "#E62250" }}>
                  *
                </span>
              )}
            </P>
          </label>
        )}

        <Controller
          name={field.name}
          control={control}
          rules={{
            required: field.required ? "Please select an option" : false,
          }}
          defaultValue={field.acceptMultiple ? [] : ""}
          render={({ field: formField }) => (
            <div className="space-y-2">
              {field.values?.map((option, index) => (
                <CustomCheckbox
                  key={index}
                  id={`${field.name}-${option.value}`}
                  label={option.label}
                  checked={
                    field.acceptMultiple
                      ? formField.value?.includes(option.value)
                      : formField.value === option.value
                  }
                  onChange={(e) => {
                    if (field.acceptMultiple) {
                      const newValue = e.target.checked
                        ? [...(formField.value || []), option.value]
                        : (formField.value || []).filter((v) => v !== option.value);
                      formField.onChange(newValue);
                    } else {
                      // For single selection, treat it like a radio button
                      formField.onChange(e.target.checked ? option.value : "");
                    }
                  }}
                />
              ))}
            </div>
          )}
        />
      </>
    );
  }

  return <div>{`Can't render field (${field.name}:${field.input})`}</div>;
};

const FormGenerator = ({ fields }) => {
  const { control } = useFormContext();
  return (
    <>
      {fields.map((field) => (
        <ErrorWrapper key={field.name} name={field.name}>
          <FormField key={field.key} field={field} control={control} />
        </ErrorWrapper>
      ))}
    </>
  );
};

export default FormGenerator;
